import React, { FC, useEffect, useState } from "react";
import {
  Autocomplete,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Form } from "assets/styleds";
import { Controller, useForm } from "react-hook-form";
import {
  EAcaoLogUser,
  EGrupoAcaoLogUser,
  ELogUserStatus,
  EUserType,
  IFiltroCurso,
  IMatricula,
  IProfessor,
  IQuestAvaliacao,
  IQuestionario,
  schemaQuestAvaliacao,
  yupQuestAvaliacao,
} from "@deltasge/marauders-map";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppSelector } from "store";
import { DatePicker } from "@mui/x-date-pickers";
import { snack } from "components/GlobalSnackbar";
import { getError } from "utils";
import { api } from "configs";
import { gravaLogUser } from "configs/log";

export const CadastroAvaQuestionario: FC<{
  handleClose: (reload?: boolean) => void;
  item?: Partial<IQuestAvaliacao>;
  questionarios: IQuestionario[];
  curso: IFiltroCurso;
  matriculas: IMatricula[];
  professores: Partial<IProfessor>[];
}> = ({ handleClose, item, curso, questionarios, matriculas, professores }) => {
  const [saving, setSaving] = useState(false);

  const {
    configuracoes: { etapas: todasAsEtapas },
    sistemaAvaliacao: { data: sistemaAvaliacao },
    escola,
    usuario: { usuario },
  } = useAppSelector((state) => state);

  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IQuestAvaliacao>({
    defaultValues: {
      ...yupQuestAvaliacao.getDefault(),
      ...item,
      idAluno: item?.idAluno ? item.idAluno : [],
      bimestre: item?.bimestre != undefined ? item.bimestre : escola.bimestre,
      idProfessor:
        usuario?.tipoUsuario == EUserType.Professor && usuario?.id
          ? usuario.id
          : undefined,
    },
    resolver: yupResolver(schemaQuestAvaliacao),
  });

  const idAluno = watch("idAluno");
  const idQuestionario = watch("idQuestionario");
  const bimestre = watch("bimestre");
  const matriculasSelecionadas = matriculas.filter(
    (f) => f.aluno && f.aluno.uid && idAluno.includes(f.aluno.uid)
  );
  const etapas = todasAsEtapas.filter((f) =>
    f.idsCursos?.includes(curso.id || 0)
  );
  const questionario = questionarios.find((f) => f.id == idQuestionario);
  const isEdit = !!item?.id;

  useEffect(() => {
    if (questionario?.multiBimestre) {
      setValue("bimestre", 0);
    } else if (bimestre == 0)
      setValue("bimestre", item?.bimestre || escola.bimestre);
  }, [questionario]);

  const onSubmit = async (payload: IQuestAvaliacao) => {
    try {
      setSaving(true);
      await api.post("avaliacao-questionario/lote", payload);
      gravaLogUser({
        logKey: {
          acao: isEdit ? EAcaoLogUser.Alteracao : EAcaoLogUser.Inclusao,
          grupoAcao: EGrupoAcaoLogUser.Questionario,
        },
        logKeys: {
          idTurma: payload.idTurma,
          idSerie: payload.idSerie,
          idCurso: payload.idCurso,
        },
        status: ELogUserStatus.Aguardando,
        message: `${
          isEdit ? "alterou" : "incluiu"
        } avaliação de questionário em lote`,
        dataAfter: { ...payload },
      });
      handleClose(true);
    } catch (error) {
      snack.error(getError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <DialogContent dividers sx={{ paddingBottom: 0 }}>
        <Form noValidate id="avaQuestionario" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            {usuario?.tipoUsuario != EUserType.Professor && (
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name="idProfessor"
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      label={schemaQuestAvaliacao.fields.idProfessor.spec.label}
                      helperText={fieldState.error?.message}
                      error={!!fieldState.error}
                    >
                      {professores.map((option, index) => (
                        <MenuItem key={index} value={option.id}>
                          {option.nome}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <Controller
                control={control}
                name="idQuestionario"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    disabled={isEdit}
                    label={
                      schemaQuestAvaliacao.fields.idQuestionario.spec.label
                    }
                    helperText={fieldState.error?.message}
                    error={!!fieldState.error}
                  >
                    {questionarios.map((option, index) => (
                      <MenuItem key={index} value={option.id}>
                        {option.nome}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="data"
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    DialogProps={{ fullWidth: false }}
                    label={schemaQuestAvaliacao.fields.data.spec.label}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        helperText={errors.data?.message}
                        error={!!errors.data}
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                control={control}
                name="bimestre"
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    disabled={questionario?.multiBimestre || false}
                    select
                    label={sistemaAvaliacao?.nomeEtapa ?? "Etapa"}
                    helperText={fieldState.error?.message}
                    error={!!fieldState.error}
                    SelectProps={{
                      displayEmpty: true,
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  >
                    {questionario?.multiBimestre && (
                      <MenuItem value={0}>TODOS</MenuItem>
                    )}
                    {etapas.map((option, index) => (
                      <MenuItem key={index} value={option.etapa}>
                        {option.etapa}º {sistemaAvaliacao?.nomeEtapa}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                disabled={isEdit}
                multiple
                value={matriculasSelecionadas}
                options={matriculas}
                getOptionLabel={(option) =>
                  `${option.aluno?.nomeusual || option.aluno?.nome}`.rUpper()
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Alunos"
                    placeholder="Selecione os alunos"
                    helperText={errors.idAluno?.message}
                    error={!!errors.idAluno}
                  />
                )}
                onChange={(e, value) => {
                  setValue(
                    "idAluno",
                    value.map((m) => m.aluno?.uid || 0)
                  );
                }}
              />
            </Grid>
          </Grid>
        </Form>
      </DialogContent>
      <DialogActions>
        <Button
          variant="text"
          color="inherit"
          onClick={() => handleClose(false)}
        >
          Fechar
        </Button>
        <LoadingButton type="submit" loading={saving} form="avaQuestionario">
          Salvar
        </LoadingButton>
      </DialogActions>
    </>
  );
};
